import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const SOURCES = ['MANUAL', 'GOODS_RECEIPT', 'PRODUCTION'];
const empty = () => ({ productId: '', warehouseId: '', quantity: '', source: 'GOODS_RECEIPT' });

export function InspectionLotModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [chars, setChars] = useState([{ name: '', result: '' }]);
  const [error, setError] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });
  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setChars([{ name: '', result: '' }]); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChar = (i, k) => (e) => setChars((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: e.target.value } : c)));

  const save = useMutation({
    mutationFn: (body) => inventoryApi.createInspectionLot(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory', 'inspection-lots'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.productId || !form.warehouseId || !form.quantity || Number(form.quantity) <= 0) { setError(t('qm.error.required')); return; }
    const characteristics = chars.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), result: c.result || null }));
    save.mutate({ productId: form.productId, warehouseId: form.warehouseId, quantity: Number(form.quantity), source: form.source, characteristics });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('qm.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('qm.field.product')}</span>
          <select className="input" value={form.productId} onChange={set('productId')} required>
            <option value="">-</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('qm.field.warehouse')}</span>
            <select className="input" value={form.warehouseId} onChange={set('warehouseId')} required>
              <option value="">-</option>
              {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
            </select>
          </label>
          <label className="field" style={{ maxWidth: 120 }}>
            <span className="field-label">{t('qm.field.quantity')}</span>
            <input className="input num" type="number" min="0.0001" step="any" value={form.quantity} onChange={set('quantity')} required />
          </label>
          <label className="field" style={{ maxWidth: 170 }}>
            <span className="field-label">{t('qm.field.source')}</span>
            <select className="input" value={form.source} onChange={set('source')}>
              {SOURCES.map((s) => <option key={s} value={s}>{t(`qm.source.${s}`)}</option>)}
            </select>
          </label>
        </div>

        <div className="field">
          <span className="field-label">{t('qm.field.characteristics')}</span>
          {chars.map((c, i) => (
            <div key={i} className="field-row" style={{ marginBottom: 'var(--space-2)' }}>
              <input className="input" placeholder={t('qm.field.charName')} value={c.name} onChange={setChar(i, 'name')} />
              <select className="input" style={{ maxWidth: 110 }} value={c.result} onChange={setChar(i, 'result')}>
                <option value="">-</option>
                <option value="OK">{t('qm.result.OK')}</option>
                <option value="NOK">{t('qm.result.NOK')}</option>
              </select>
            </div>
          ))}
          <Button type="button" size="sm" variant="ghost" onClick={() => setChars((cs) => [...cs, { name: '', result: '' }])}>{t('qm.field.addChar')}</Button>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
